
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash, Plus, Save } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Option {
  id?: string;
  text: string;
  is_correct: boolean;
  question_id?: string;
}

interface Question {
  id: string;
  text: string;
  explanation?: string;
  options: Option[];
}

interface QuestionEditorProps {
  question: Question;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, onSave, onCancel }) => {
  const { toast } = useToast();
  const [questionText, setQuestionText] = useState(question.text);
  const [explanation, setExplanation] = useState(question.explanation || "");
  const [options, setOptions] = useState<Option[]>([]);
  const [touched, setTouched] = useState(false);
  const [deletedOptionIds, setDeletedOptionIds] = useState<string[]>([]);
  const [correctOptionIndex, setCorrectOptionIndex] = useState<number>(-1);

  useEffect(() => {
    // Initialize options from the question
    const formattedOptions = question.options.map(option => ({
      id: option.id,
      text: option.text,
      is_correct: option.is_correct,
      question_id: question.id
    }));
    
    setOptions(formattedOptions);
    
    // Set the correct option index
    const correctIndex = formattedOptions.findIndex(opt => opt.is_correct);
    setCorrectOptionIndex(correctIndex !== -1 ? correctIndex : 0);
    
    // Reset deleted option IDs when question changes
    setDeletedOptionIds([]);
  }, [question]);

  const handleOptionTextChange = (index: number, text: string) => {
    const newOptions = [...options];
    newOptions[index].text = text;
    setOptions(newOptions);
    setTouched(true);
  };

  const handleOptionCorrectChange = (index: number) => {
    setCorrectOptionIndex(index);
    
    // Create a new array where only the selected option is marked as correct
    const newOptions = options.map((option, i) => ({
      ...option,
      is_correct: i === index
    }));
    
    setOptions(newOptions);
    setTouched(true);
  };

  const addOption = () => {
    setOptions([
      ...options, 
      { 
        text: "", 
        is_correct: false,
        question_id: question.id
      }
    ]);
    setTouched(true);
  };

  const removeOption = (index: number) => {
    const newOptions = [...options];
    const removedOption = newOptions[index];
    
    if (removedOption.id) {
      setDeletedOptionIds(prev => [...prev, removedOption.id as string]);
    }
    
    newOptions.splice(index, 1);
    
    // If removing the correct option, make the first remaining option correct
    if (index === correctOptionIndex) {
      if (newOptions.length > 0) {
        setCorrectOptionIndex(0);
        newOptions[0].is_correct = true;
      } else {
        setCorrectOptionIndex(-1);
      }
    } 
    // Adjust correctOptionIndex if we're removing an option before it
    else if (index < correctOptionIndex) {
      setCorrectOptionIndex(correctOptionIndex - 1);
    }
    
    setOptions(newOptions);
    setTouched(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!questionText.trim()) {
      toast({
        title: "Validation Error",
        description: "Question text is required",
        variant: "destructive"
      });
      return;
    }
    
    if (options.length < 2) {
      toast({
        title: "Validation Error",
        description: "At least two options are required",
        variant: "destructive"
      });
      return;
    }
    
    // Ensure all options have text
    if (options.some(opt => !opt.text.trim())) {
      toast({
        title: "Validation Error",
        description: "All options must have text",
        variant: "destructive"
      });
      return;
    }
    
    // Ensure exactly one option is marked as correct
    // This is critical - we need to update is_correct values just before submitting
    const updatedOptions = options.map((opt, idx) => ({
      ...opt,
      is_correct: idx === correctOptionIndex
    }));
    
    const dataToSubmit = {
      id: question.id,
      text: questionText,
      explanation: explanation.trim() || null,
      options: updatedOptions,
      deletedOptionIds: deletedOptionIds
    };
    
    console.log("Submitting question data:", dataToSubmit);
    onSave(dataToSubmit);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="question-text">Question Text</Label>
        <Textarea
          id="question-text"
          value={questionText}
          onChange={(e) => {
            setQuestionText(e.target.value);
            setTouched(true);
          }}
          placeholder="Enter the question text"
          className="min-h-[100px]"
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="explanation">Explanation (Optional)</Label>
        <Textarea
          id="explanation"
          value={explanation}
          onChange={(e) => {
            setExplanation(e.target.value);
            setTouched(true);
          }}
          placeholder="Enter an explanation for the correct answer"
          className="min-h-[80px]"
        />
      </div>
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Label>Answer Options</Label>
          <Button 
            type="button" 
            onClick={addOption} 
            variant="outline" 
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Option
          </Button>
        </div>
        
        <RadioGroup 
          value={correctOptionIndex.toString()} 
          onValueChange={(value) => handleOptionCorrectChange(parseInt(value))}
          className="space-y-3"
        >
          {options.map((option, index) => (
            <div key={index} className="flex items-start space-x-2 border p-3 rounded-md">
              <RadioGroupItem
                id={`option-radio-${index}`}
                value={index.toString()}
                className="mt-2"
                checked={index === correctOptionIndex}
              />
              <div className="flex-1">
                <Label htmlFor={`option-${index}`} className="mb-1 block">
                  Option {index + 1} {index === correctOptionIndex && "(Correct)"}
                </Label>
                <Input
                  id={`option-${index}`}
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(index, e.target.value)}
                  placeholder={`Enter option ${index + 1}`}
                  required
                />
              </div>
              <Button 
                type="button"
                variant="ghost" 
                size="sm" 
                onClick={() => removeOption(index)}
                disabled={options.length <= 2}
                className="mt-6"
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </RadioGroup>
      </div>
      
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={!touched}
        >
          <Save className="h-4 w-4 mr-2" /> Save Changes
        </Button>
      </div>
    </form>
  );
};

export default QuestionEditor;
